// English SEO landing page content — the canonical/default language. Every other
// locale file in this folder must have the exact same slugs and field shape.
export default {
  "instagram-downloader": {
    title: "Instagram Downloader — Download Instagram Videos & Photos Free",
    h1: "Instagram Downloader",
    desc: "Download any Instagram video, photo, reel, or carousel post for free. No login, no watermark, works on iPhone, Android, and PC.",
    metaDesc: "Free Instagram downloader — save Instagram videos, photos, reels, and carousel posts in HD. No watermark, no login. Works on iPhone, Android, and PC.",
    keywords: "instagram downloader, download instagram videos, instagram photo downloader, free instagram downloader",
    faq: [
      { q: "How do I download Instagram videos?", a: "Copy the Instagram video URL, paste it into InstantSaver, click Preview, then click Download. Your video saves in seconds." },
      { q: "Can I download Instagram photos?", a: "Yes. Paste the post URL and InstantSaver detects whether it is a photo or video and shows the correct download button." },
      { q: "Do I need to create an account?", a: "No account or login is ever needed. InstantSaver is completely free and anonymous." },
      { q: "Is there a watermark on downloaded files?", a: "No. Files are downloaded directly from Instagram's CDN in their original quality — no watermark is added." },
    ],
    featureTitle: "What You Can Download",
    features: [
      { icon: "🎬", text: "Instagram Reels — full HD video" },
      { icon: "🖼️", text: "Photos & feed posts" },
      { icon: "🗂️", text: "Carousel posts — every image individually" },
      { icon: "📺", text: "IGTV long-form videos" },
      { icon: "👤", text: "Profile pictures in original quality" },
    ],
  },

  "reels-downloader": {
    title: "Instagram Reels Downloader — Save Reels in HD Free",
    h1: "Instagram Reels Downloader",
    desc: "Save any Instagram Reel to your device in HD quality. No watermark, no sign-up, works on any device instantly.",
    metaDesc: "Download Instagram Reels in HD for free. No watermark, no login required. Save reels to iPhone, Android, or PC in seconds with InstantSaver.",
    keywords: "instagram reels downloader, download instagram reels, save reels, reels saver, reels download free",
    faq: [
      { q: "How do I download Instagram Reels?", a: "Open the Reel on Instagram, tap the three-dot menu (⋯) and copy the link. Paste it into InstantSaver and click Download." },
      { q: "Can I download Reels on iPhone?", a: "Yes. InstantSaver works in Safari on iPhone. After downloading, the video is in your Files or Photos app." },
      { q: "Can I download Reels on Android?", a: "Yes. The video saves to your device's Downloads folder automatically." },
      { q: "Are downloaded Reels HD quality?", a: "Yes. Reels are fetched at their original resolution — up to 1080p when available." },
    ],
    featureTitle: "Why Use InstantSaver for Reels?",
    features: [
      { icon: "⚡", text: "Download any public Reel in under 5 seconds" },
      { icon: "🔇", text: "No watermark — clean original file" },
      { icon: "📱", text: "Works on iPhone, Android, tablet, and desktop" },
      { icon: "🔒", text: "No login or account needed — ever" },
      { icon: "♾️", text: "Unlimited downloads, no daily cap" },
    ],
  },

  "youtube-downloader": {
    title: "YouTube Video Downloader — Download YouTube Videos Free",
    h1: "YouTube Video Downloader",
    desc: "Download YouTube videos and Shorts for free. Save MP4 video or audio directly to your device — fast and easy.",
    metaDesc: "Free YouTube video downloader. Download YouTube videos and Shorts in MP4 or audio-only format. No sign-up needed. Fast and simple.",
    keywords: "youtube downloader, download youtube videos, youtube video downloader, youtube shorts downloader, download youtube mp4",
    faq: [
      { q: "How do I download YouTube videos?", a: "Switch to the YouTube tab on InstantSaver, paste the video URL, click Preview, then click Download." },
      { q: "Can I download YouTube Shorts?", a: "Yes. Paste the Shorts URL and InstantSaver handles the download automatically." },
      { q: "What quality is the download?", a: "InstantSaver downloads the best available pre-muxed quality, typically up to 720p." },
      { q: "Can I download just the audio?", a: "Yes. After previewing a video, click the Download Audio button to get the M4A audio track." },
    ],
    featureTitle: "YouTube Download Options",
    features: [
      { icon: "🎥", text: "MP4 video — 360p, 480p, 720p options" },
      { icon: "🎵", text: "Audio-only M4A download" },
      { icon: "⚡", text: "YouTube Shorts — same as any video" },
      { icon: "🔒", text: "No account required" },
      { icon: "📱", text: "Mobile and desktop compatible" },
    ],
  },

  "profile-picture-downloader": {
    title: "Instagram Profile Picture Downloader — Save HD Profile Photo",
    h1: "Instagram Profile Picture Downloader",
    desc: "Download anyone's Instagram profile picture in full size. Just paste the profile URL and save the DP instantly.",
    metaDesc: "Download Instagram profile pictures (DPs) in original quality. Paste any public Instagram profile URL and save the photo to your device free.",
    keywords: "instagram profile picture downloader, instagram dp downloader, download instagram profile photo, instagram profile pic saver",
    faq: [
      { q: "How do I download an Instagram profile picture?", a: "Go to the Instagram profile, copy the URL (e.g. instagram.com/username), paste it into InstantSaver, and click Download Profile Picture." },
      { q: "Can I download a private account's profile picture?", a: "No. Only public account profile pictures are accessible without logging in." },
      { q: "Is the downloaded photo the full-size version?", a: "InstantSaver downloads the highest quality image available from Instagram's public CDN." },
      { q: "What format is the downloaded profile picture?", a: "Profile pictures are saved as JPEG files." },
    ],
    featureTitle: "How It Works",
    features: [
      { icon: "👤", text: "Works for any public Instagram account" },
      { icon: "🖼️", text: "Downloads the highest available quality" },
      { icon: "⚡", text: "Instant preview before download" },
      { icon: "🔒", text: "No login — anonymous and private" },
      { icon: "📱", text: "Works on all devices and browsers" },
    ],
  },

  "carousel-downloader": {
    title: "Instagram Carousel Downloader — Download All Carousel Photos & Videos",
    h1: "Instagram Carousel Downloader",
    desc: "Download every photo and video from an Instagram carousel post individually or all at once with one click.",
    metaDesc: "Download all photos and videos from Instagram carousel posts. Save each item individually or use Download All. Free, fast, no watermark.",
    keywords: "instagram carousel downloader, download instagram carousel, instagram multiple photos download, carousel post saver, instagram album downloader",
    faq: [
      { q: "Can I download all carousel images at once?", a: 'Yes. After pasting a carousel URL, InstantSaver shows every item with individual download buttons plus a "Download All" button.' },
      { q: "What if a carousel has videos and photos mixed?", a: "InstantSaver detects each item type automatically — photos save as JPG, videos save as MP4." },
      { q: "Is there a limit on how many items I can download?", a: "No limit. InstantSaver can detect and download carousels with up to 20 items." },
      { q: "How do I get the carousel link?", a: "On Instagram, open the post, tap ⋯ and choose Copy Link. The URL contains /p/ — that's a post, which can be a carousel." },
    ],
    featureTitle: "Carousel Download Features",
    features: [
      { icon: "🗂️", text: "Detects all carousel items automatically" },
      { icon: "⬇️", text: "Individual download button for each item" },
      { icon: "📦", text: "Download All button for one-click bulk save" },
      { icon: "🎬", text: "Handles mixed photo + video carousels" },
      { icon: "✨", text: "Preview thumbnails before downloading" },
    ],
  },

  "stories-downloader": {
    title: "Instagram Story Downloader — Save Stories Before They Expire",
    h1: "Instagram Story Downloader",
    desc: "Save any public Instagram Story — photo or video — to your device before it disappears in 24 hours. No login required.",
    metaDesc: "Download Instagram Stories for free. Save public story photos and videos before they expire. No login, no watermark, works on any device.",
    keywords: "instagram story downloader, download instagram story, save instagram story, instagram story saver",
    faq: [
      { q: "How do I download an Instagram Story?", a: "Open the Story on Instagram, copy its link (or the profile link), paste it into InstantSaver, and click Download." },
      { q: "Can I download a Story anonymously?", a: "Yes. InstantSaver fetches public Stories without logging in, and the account owner is not notified." },
      { q: "Can I download Stories from a private account?", a: "No. Only Stories on public accounts can be accessed without logging in." },
      { q: "What happens after a Story expires?", a: "Once a Story is gone from Instagram (after 24 hours, unless saved as a Highlight), InstantSaver can no longer fetch it — download it before then." },
    ],
    featureTitle: "Why Use InstantSaver for Stories",
    features: [
      { icon: "⏱️", text: "Save Stories before the 24-hour expiry" },
      { icon: "🙈", text: "Anonymous — the poster is never notified" },
      { icon: "🎬", text: "Works for both photo and video Stories" },
      { icon: "🔒", text: "No login or account needed" },
      { icon: "📱", text: "Works on iPhone, Android, and PC" },
    ],
  },

  "highlights-downloader": {
    title: "Instagram Highlights Downloader — Download Saved Story Highlights",
    h1: "Instagram Highlights Downloader",
    desc: "Download videos and photos from a public Instagram profile's Story Highlights — the saved stories pinned to a profile.",
    metaDesc: "Download Instagram Highlights for free. Save individual photos and videos from a public profile's pinned Story Highlights.",
    keywords: "instagram highlights downloader, download instagram highlights, instagram highlight saver, save instagram highlights",
    faq: [
      { q: "What are Instagram Highlights?", a: "Highlights are Stories a user has chosen to keep permanently pinned to their profile, grouped into named collections." },
      { q: "How do I download a Highlight?", a: "Open the Highlight on the profile, copy its link, paste it into InstantSaver, and click Download." },
      { q: "Can I download every item in a Highlight at once?", a: "Yes, where Instagram allows it — InstantSaver shows each slide with its own download button." },
      { q: "Do I need to follow the account to download their Highlights?", a: "No, as long as the account is public." },
    ],
    featureTitle: "Highlights Download Features",
    features: [
      { icon: "📌", text: "Works on any public profile's pinned Highlights" },
      { icon: "🎞️", text: "Downloads individual photo and video slides" },
      { icon: "🔒", text: "No login or account needed" },
      { icon: "📱", text: "Works on iPhone, Android, and PC" },
    ],
  },
};
